import { GraduationCap, TrendingUp, Globe2, Trophy, Sparkles, ArrowRight, PlayCircle, Users, DollarSign, Briefcase, Mail, Info, Video, Rocket } from 'lucide-react';
import { useState, useEffect } from 'react';
import { EventsSection } from './EventsSection';
import { TutorialsSection } from './TutorialsSection';
import { API_URL } from '../config/api';
import ClubViajemosLogo from './ClubViajemosLogo';

const HERO_IMG = "https://images.unsplash.com/photo-1758691737584-a8f17fb34475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TRAVEL_IMG = "https://images.unsplash.com/photo-1769175053901-1ab89766723d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const PILLARS = [
  { Icon: GraduationCap, title: "Educación de verdad", desc: "Clases prácticas que te llevan de cero a tu primera venta y más allá.", color: "#45A0EE" },
  { Icon: TrendingUp, title: "Poder financiero", desc: "Aprende a construir ingresos que crecen mes a mes con comisiones y override.", color: "#80C753" },
  { Icon: Globe2, title: "Agentes que viajan", desc: "Vive la industria desde adentro: viaja con precios de agente mientras facturas.", color: "#E3C766" },
];

const CLASSES = [
  { tag: "Fundamentos", title: "Los 5 Secretos Vacacionales", desc: "La base del negocio: cómo funciona la industria y por dónde entra tu dinero.", color: "#45A0EE" },
  { tag: "Ventas", title: "Cierra tu primera venta", desc: "Scripts, objeciones y el paso a paso para cobrar tu primera comisión.", color: "#80C753" },
  { tag: "Crecimiento", title: "El poder del Override", desc: "Cómo formar agentes y cobrar por cada venta de tu equipo, para siempre.", color: "#E3C766" },
  { tag: "Marketing", title: "Atrae clientes 24/7", desc: "Construye tu máquina de prospectos con contenido y campañas simples.", color: "#C084FC" },
  { tag: "Finanzas", title: "Finanzas del agente exitoso", desc: "Administra, reinvierte y escala tus ingresos como un verdadero dueño.", color: "#F59E0B" },
  { tag: "Mentalidad", title: "Mentalidad de poder", desc: "Hábitos y disciplina de las personas que logran libertad financiera.", color: "#34D399" },
];

const EducacionPage = () => {
  const [eduTutorials, setEduTutorials] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/tutorials`, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (alive && Array.isArray(data)) {
            setEduTutorials(data.filter((t) => (t.location || 'home') === 'educacion'));
          }
        }
      } catch (e) { console.error('Edu tutorials fetch failed:', e); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#070F20] text-white" data-testid="educacion-page">
      {/* Top bar */}
      <header className="relative z-20 flex items-center justify-between px-5 sm:px-10 py-5">
        <a href="/" data-testid="educacion-logo"><ClubViajemosLogo /></a>
        <a href="/planes" data-testid="educacion-nav-planes" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 px-5 py-2.5 text-sm font-bold transition-colors">
          Ver planes <ArrowRight className="h-4 w-4" />
        </a>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden px-5 sm:px-10 pt-8 pb-8 sm:pt-12 sm:pb-10">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg,#070F20 10%,rgba(7,15,32,0.72) 55%,rgba(7,15,32,0.45) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E3C766]/15 border border-[#E3C766]/40 text-[#E3C766] px-4 py-1.5 text-[12px] font-black uppercase tracking-[2px] mb-6">
            <Sparkles className="h-3.5 w-3.5" /> Academia Club Viajemos
          </span>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[1.02] tracking-tight">
            Educación que te hace <span className="text-[#80C753]">libre</span> y <span className="text-[#E3C766]">poderoso</span>
          </h1>
          <p className="text-base sm:text-xl text-slate-300 mt-6 max-w-2xl mx-auto leading-relaxed">
            Aprende de quienes ya lo lograron. Clases en vivo, mentores reales y un camino claro para ganar dinero en la industria de viajes… mientras viajas por el mundo.
          </p>
        </div>
      </section>

      {/* AVISO IMPORTANTE DE SABER */}
      <section className="px-5 sm:px-10 pt-2 pb-2 max-w-6xl mx-auto" data-testid="educacion-aviso-importante">
        <div className="relative overflow-hidden rounded-3xl border border-[#E3C766]/50 bg-gradient-to-br from-[#2a2208] via-[#1a1606] to-[#0d0b04] p-6 sm:p-8">
          <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-[#E3C766]/15 blur-3xl pointer-events-none" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E3C766] text-[#1a1606] px-4 py-1.5 text-[11px] font-black uppercase tracking-[2px] mb-5">
              <Info className="h-3.5 w-3.5" /> Importante de saber
            </span>

            <div className="grid md:grid-cols-2 gap-5">
              {/* Email */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="w-9 h-9 rounded-xl bg-[#45A0EE]/20 border border-[#45A0EE]/40 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4.5 w-4.5 text-[#45A0EE]" />
                  </span>
                  <h3 className="text-base sm:text-lg font-black text-white">El email: tu canal más confiable</h3>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  El sistema <span className="font-bold text-white">más antiguo, más confiable y más fácil</span> sigue siendo el email. Cuando tengas una pregunta, escríbenos a{' '}
                  <a href="mailto:info@clubviajemos.com" data-testid="educacion-email-link" className="font-black text-[#45A0EE] underline underline-offset-2 hover:text-[#6fb6f2]">info@clubviajemos.com</a>.
                  Nosotros te contestaremos y así mejorarás tu camino al éxito.
                </p>
              </div>

              {/* Academia */}
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                <div className="flex items-center gap-2.5 mb-2">
                  <span className="w-9 h-9 rounded-xl bg-[#80C753]/20 border border-[#80C753]/40 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="h-4.5 w-4.5 text-[#80C753]" />
                  </span>
                  <h3 className="text-base sm:text-lg font-black text-white">Entra siempre a la Academia</h3>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Lo más importante es entrar siempre a la <a href="/academia" data-testid="educacion-aviso-academia-link" className="font-black text-[#80C753] underline underline-offset-2 hover:text-[#9bd872]">ACADEMIA</a>. Con esta fórmula tu crecimiento está asegurado y no te puedes perder ningún evento de esta sección.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-[#E3C766]/10 border border-[#E3C766]/30 p-4">
              <Video className="h-5 w-5 text-[#E3C766] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-200 leading-relaxed">
                Además, <span className="font-bold text-white">grabaremos cada Zoom</span>, que quedará registrado en la Academia para que lo veas cuando quieras.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EVENTOS DE EDUCACIÓN (dinámicos desde administración) — arriba */}
      <section id="eventos-educacion" className="px-5 sm:px-10 pt-4 sm:pt-6 pb-4 max-w-6xl mx-auto scroll-mt-20" data-testid="educacion-eventos">
        <div className="bg-white/[0.02] border border-white/10 rounded-3xl p-5 sm:p-8">
          <EventsSection
            location="educacion"
            title="Próximas clases en vivo"
            subtitle="Clases y capacitaciones del programa educativo"
          />
        </div>
        {/* Botón Academia debajo de los eventos */}
        <div className="flex justify-center mt-8">
          <a href="/academia" data-testid="educacion-cta-academia" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#80C753] to-[#45A0EE] px-9 py-4 font-black text-[#07131f] hover:opacity-90 transition-opacity">
            <GraduationCap className="h-5 w-5" /> Academia
          </a>
        </div>
      </section>

      {/* VIDEOS DE EDUCACIÓN (colgados desde administración global) */}
      {eduTutorials.length > 0 && (
        <section className="px-5 sm:px-10 pt-2 pb-6 max-w-6xl mx-auto" data-testid="educacion-videos">
          <TutorialsSection tutorials={eduTutorials} variant="educacion" />
        </section>
      )}

      {/* PILARES */}
      <section className="px-5 sm:px-10 py-16 sm:py-20 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map(({ Icon, title, desc, color }) => (
            <div key={title} className="rounded-3xl bg-white/[0.03] border border-white/10 p-8 hover:-translate-y-1.5 transition-transform">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${color}1f`, border: `1px solid ${color}55` }}>
                <Icon className="h-7 w-7" style={{ color }} />
              </div>
              <h3 className="text-xl font-black mb-2">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLASES QUE IMPARTIMOS */}
      <section className="px-5 sm:px-10 py-6 sm:py-10 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <span className="text-[12px] font-black uppercase tracking-[2px] text-[#45A0EE]">El programa</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2">Las clases que impartimos</h2>
            <p className="text-slate-400 mt-3 max-w-2xl">Un plan de estudio pensado para llevarte de principiante a dueño de tu propia agencia.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CLASSES.map(({ tag, title, desc, color }) => (
            <div key={title} className="group rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden hover:border-white/25 transition-colors">
              <div className="h-1.5 w-full" style={{ backgroundColor: color }} />
              <div className="p-6">
                <span className="inline-block text-[10px] font-black uppercase tracking-[1.5px] mb-3 px-2.5 py-1 rounded-full" style={{ backgroundColor: `${color}1f`, color }}>{tag}</span>
                <h3 className="text-lg font-black mb-2 leading-snug">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FRANJA VIAJE */}
      <section className="relative my-16 sm:my-20 mx-5 sm:mx-10 rounded-3xl overflow-hidden">
        <img src={TRAVEL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#070F20ee 0%,#070F20aa 45%,transparent 100%)" }} />
        <div className="relative z-10 p-8 sm:p-14 max-w-xl">
          <h2 className="text-2xl sm:text-4xl font-black leading-tight">No solo aprendes a vender viajes. <span className="text-[#E3C766]">Los vives.</span></h2>
          <p className="text-slate-200 mt-4">Nuestros agentes recorren el mundo con precios de agente mientras construyen ingresos. La educación es el primer boleto.</p>

          <div className="mt-6 rounded-2xl border border-[#E3C766]/35 bg-[#070F20]/70 backdrop-blur-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="h-5 w-5 text-[#E3C766]" />
              <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#E3C766]">Tu despegue: Training de 30 días</span>
            </div>
            <p className="text-white font-bold text-base sm:text-lg leading-snug">
              Arrancas con un <span className="text-[#E3C766]">training de ventas de 30 días</span> junto al departamento de ventas de Club Viajemos.
            </p>
            <p className="text-slate-300 text-sm mt-1.5">
              El equipo que más vende te acompaña paso a paso para que cierres tus primeras ventas. Esto es lo que hará tu camino más fácil — no empiezas solo, empiezas acompañado.
            </p>
            <a
              href="/training"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="educacion-training-link"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E3C766] to-[#c9a84c] px-6 py-2.5 font-black text-[#07131f] text-sm hover:opacity-90 transition-opacity"
            >
              Ver el programa de 30 días <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-5 sm:px-10 pb-24 max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-black">Tu próxima versión empieza con una clase.</h2>
        <p className="text-slate-400 mt-4">Elige tu camino en la industria de viajes y deja que la educación haga el resto.</p>
        <a href="/planes" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E3C766] to-[#c9a84c] px-9 py-4 font-black text-[#07131f] hover:opacity-90 transition-opacity">
          Ver los planes <ArrowRight className="h-5 w-5" />
        </a>
      </section>

      <footer className="bg-[#040913] py-6 px-5 text-center border-t border-white/5">
        <p className="text-[13px] text-slate-400">Club Viajemos · Travel Empire — Academia. Licencias y Registros ST39568.</p>
      </footer>
    </div>
  );
};

export default EducacionPage;
